import { DotsThreeIcon } from '@phosphor-icons/react';
import * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { countCharacters, countWords } from '@/lib/concept-copy/text-metrics';
import { ICON_BUTTON, META_SEPARATOR, PROPERTY_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface CopyVariantCardProps {
  /** The field this option fills — `Headline`, `Primary text`, `CTA`. */
  field: string;
  text: string;
  onTextChange?: (next: string) => void;
  /**
   * Tone from the generation metadata. OMITTED entirely when the generator did
   * not report one — an invented tone label is a hallucination on a surface a
   * marketer will trust.
   */
  tone?: string;
  /** Platform character ceiling. Over it, the count turns destructive. */
  maxChars?: number;
  /** Where the platform folds the text behind "see more". Past it is a note, not an error. */
  foldChars?: number;
  /** Named for the truncation warning, e.g. "Meta". */
  platformLabel?: string;
  onMoreLikeThis?: () => void;
  moreLikeThisDisabled?: boolean;
  /** Extra `⋯` entries — rewrite, copy, reset. Store-owned. */
  actions?: React.ReactNode;
  readOnly?: boolean;
  className?: string;
}

/**
 * A generated copy option, headed by the constraints a marketer actually cares
 * about: `56 characters · 27 words · Casual, fun`.
 *
 * This metadata is real information, not decoration — ad platforms enforce hard
 * character limits, so the count tells you at a glance whether an option will
 * truncate before you ever leave the workbench. Mora has shipped nothing like
 * it, and it is close to free.
 *
 * `More like this` is the second borrowed mechanism: regeneration anchored on
 * ONE option rather than re-rolling the whole set, so a user climbs toward a
 * good result instead of gambling repeatedly.
 */
export function CopyVariantCard({
  field,
  text,
  onTextChange,
  tone,
  maxChars,
  foldChars,
  platformLabel,
  onMoreLikeThis,
  moreLikeThisDisabled,
  actions,
  readOnly,
  className,
}: CopyVariantCardProps): JSX.Element {
  // Grapheme-safe, shared with the generation layer. `text.length` counts an
  // emoji as 2 and a flag as 4, so a naive count disagreed with the service
  // that decides the truncation warning — the card would have claimed a caption
  // fit while the platform truncated it.
  const chars = countCharacters(text);
  const words = countWords(text);
  const overLimit = typeof maxChars === 'number' && chars > maxChars;

  return (
    <article className={cn('space-y-1', className)}>
      <div
        className={cn(
          'flex min-h-8 items-start gap-2 rounded-sm bg-muted px-2.5 py-1.5 dark:bg-muted/40',
          'transition-colors focus-within:bg-muted-foreground/10',
        )}
      >
        {/* PROPERTY_LABEL, not SECTION_LABEL: the tracked-out caps eyebrow is
            already doing duty one level up as the section header. Reusing it
            inside the row gives the column two shouting levels and the eye
            cannot tell which one is the heading. */}
        <span className={cn(PROPERTY_LABEL, 'w-16 pt-1')}>{field}</span>
        {readOnly || !onTextChange ? (
          <p className="min-w-0 flex-1 text-caption text-foreground/85">{text}</p>
        ) : (
          <textarea
            aria-label={field}
            value={text}
            rows={1}
            onChange={(event) => onTextChange(event.target.value)}
            className="min-w-0 flex-1 resize-none bg-transparent text-caption text-foreground/85 outline-none field-sizing-content"
          />
        )}
      </div>

      <div className="flex items-start gap-2 px-2.5">
        <p className="min-w-0 flex-1 text-micro text-muted-foreground">
          <span className={cn('tabular-nums', overLimit && 'text-destructive')}>
            {overLimit ? `${chars} / ${maxChars} characters` : `${chars} characters`}
          </span>
          <span className={cn('mx-1', META_SEPARATOR)}>·</span>
          <span className="tabular-nums">
            {words} {words === 1 ? 'word' : 'words'}
          </span>
          {tone ? (
            <>
              <span className={cn('mx-1', META_SEPARATOR)}>·</span>
              <span>{tone}</span>
            </>
          ) : null}
          {overLimit && platformLabel ? (
            /* Its own line, not a fourth `·` segment. In the 300px inspector the
               four-part row always wrapped, and it wrapped MID-PHRASE — the
               separator dangled at the end of one line and "truncate on Google"
               began the next. Seen on the live route in both themes; a sentence
               that important cannot be the thing that breaks. */
            <span className="mt-0.5 block text-destructive">Will truncate on {platformLabel}</span>
          ) : null}
          {!overLimit && platformLabel && typeof foldChars === 'number' && chars > foldChars ? (
            <span className="mt-0.5 block">
              Shows ~{foldChars} characters before &ldquo;see more&rdquo; on {platformLabel}
            </span>
          ) : null}
        </p>

        {onMoreLikeThis || actions ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={`Options for ${field}`}
              className={cn(
                ICON_BUTTON,
                'inline-flex size-6 shrink-0 -translate-y-1 items-center justify-center rounded-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <DotsThreeIcon className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              {onMoreLikeThis ? (
                <DropdownMenuItem
                  disabled={moreLikeThisDisabled}
                  onSelect={onMoreLikeThis}
                  className="gap-1.5 rounded-sm px-2 py-1.5 text-caption"
                >
                  More like this
                </DropdownMenuItem>
              ) : null}
              {actions}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </article>
  );
}
